import React, { useEffect, useRef, useState } from "react"

import { Box, X } from "lucide-react"

interface ParcelSortProps {
  sortType: "asc" | "desc"
  question: string
  rangeStart: number
  rangeEnd: number
  displayCount: number
  onComplete: (score: number, timeTaken: number) => void
}

interface Parcel {
  value: number
  placed: boolean
  id: number
}

interface ParcelBin {
  value: number | null
  isCorrect: boolean
}

const ParcelSort: React.FC<ParcelSortProps> = ({
  sortType,
  question,
  rangeStart,
  rangeEnd,
  displayCount,
  onComplete,
}) => {
  const [parcels, setParcels] = useState<Parcel[]>([])
  const [parcelBins, setParcelBins] = useState<ParcelBin[]>([])
  const [nextExpectedNumber, setNextExpectedNumber] = useState<number | null>(null)
  const [score, setScore] = useState(0)
  const [isStarted, setIsStarted] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [startTime, setStartTime] = useState<number | null>(null)
  const [_timeTaken, setTimeTaken] = useState<number>(0)
  const [incorrectParcel, setIncorrectParcel] = useState<number | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsStarted(false)
    setIsComplete(false)
    setScore(0)
    setParcels([])
    setParcelBins([])
    setNextExpectedNumber(null)
    setStartTime(null)
    setTimeTaken(0)
  }, [question])

  // Initialize the task
  useEffect(() => {
    if (isStarted) {
      // Generate unique random numbers
      const availableNumbers: number[] = []
      for (let i = rangeStart; i <= rangeEnd; i++) {
        availableNumbers.push(i)
      }

      // Shuffle the array
      for (let i = availableNumbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[availableNumbers[i], availableNumbers[j]] = [availableNumbers[j], availableNumbers[i]]
      }

      // Take only displayCount numbers
      const selectedNumbers = availableNumbers.slice(0, displayCount)

      // Create parcel objects
      const newParcels = selectedNumbers.map((value, index) => ({
        value,
        placed: false,
        id: index,
      }))

      // Create empty bins
      const newBins = Array(displayCount)
        .fill(null)
        .map(() => ({
          value: null,
          isCorrect: false,
        }))

      setParcels(newParcels)
      setParcelBins(newBins)

      // Find the minimum value to set as the first expected number
      const sortedNumbers = [...selectedNumbers].sort((a, b) =>
        sortType === "asc" ? a - b : b - a,
      )
      setNextExpectedNumber(sortedNumbers[0])
    }
  }, [isStarted, rangeStart, rangeEnd, displayCount, sortType])

  const startTask = () => {
    setIsStarted(true)
    setStartTime(Date.now())
  }

  const handleParcelClick = (parcel: Parcel) => {
    if (isComplete || parcel.placed) return

    // Find the next empty bin
    const nextEmptyBinIndex = parcelBins.findIndex((bin) => bin.value === null)
    if (nextEmptyBinIndex === -1) return // No empty bins

    // Check if this is the correct next parcel (in ascending order)
    const isCorrect = parcel.value === nextExpectedNumber

    if (isCorrect) {
      // Correct parcel selected
      setScore((prevScore) => prevScore + 1)

      // Mark this parcel as placed
      setParcels((prevParcels) =>
        prevParcels.map((p) => (p.value === parcel.value ? { ...p, placed: true } : p)),
      )

      // Place the parcel in the bin
      setParcelBins((prevBins) => {
        const newBins = [...prevBins]
        newBins[nextEmptyBinIndex] = { value: parcel.value, isCorrect: true }
        return newBins
      })

      // Find the next expected number (next smallest unplaced number)
      const remainingParcels = parcels
        .filter((p) => !p.placed && p.value !== parcel.value)
        .map((p) => p.value)

      if (remainingParcels.length === 0) {
        // All parcels have been placed
        finishTask()
      } else {
        const nextParcelNumber =
          sortType === "asc" ? Math.min(...remainingParcels) : Math.max(...remainingParcels)
        setNextExpectedNumber(nextParcelNumber)
      }
    } else {
      // Incorrect parcel selected - only show feedback and deduct points
      setScore((prevScore) => prevScore - 0.5)

      // Set the incorrect parcel to show the X icon
      setIncorrectParcel(parcel.id)

      // Clear the incorrect parcel after a short delay
      setTimeout(() => {
        setIncorrectParcel(null)
      }, 800) // Show the X for 800ms
    }

    // Check if all parcels have been placed
    const updatedParcels = parcels.map((p) =>
      p.value === parcel.value ? { ...p, placed: true } : p,
    )

    if (updatedParcels.every((p) => p.placed)) {
      finishTask()
    }
  }

  const finishTask = () => {
    setIsComplete(true)
    const endTime = Date.now()
    const calculatedTimeTaken = startTime ? (endTime - startTime) / 1000 : 0
    setTimeTaken(calculatedTimeTaken)
    onComplete(score, calculatedTimeTaken)
  }

  return (
    <div className="space-y-6">
      <h2 className="mb-6 text-2xl font-semibold">{question}</h2>

      {!isStarted ? (
        <div className="text-center">
          <button
            onClick={startTask}
            className="rounded-lg bg-[#0F1D21] px-6 py-2 font-bold text-white shadow-md transition-colors duration-300 hover:bg-gray-700"
          >
            Start Task
          </button>
        </div>
      ) : (
        <div className="space-y-6" ref={containerRef}>
          {/* Parcels Grid */}
          <div className="mb-8 grid grid-cols-3 gap-4 rounded-lg border border-dashed p-4 md:grid-cols-6">
            {parcels.map((parcel, index) => (
              <div
                key={index}
                className={`border-2 ${
                  parcel.placed
                    ? "border-gray-400 opacity-50"
                    : incorrectParcel === parcel.id
                      ? "border-red-500"
                      : "border-gray-300"
                } flex h-[80px] w-[80px] flex-col items-center justify-between rounded-lg p-2 ${
                  parcel.placed ? "cursor-default" : "cursor-pointer hover:scale-105"
                } transition-all duration-300`}
                style={{
                  backgroundColor: parcel.placed
                    ? "#D3D3D3"
                    : incorrectParcel === parcel.id
                      ? "#FEE2E2" // Light red background
                      : "#FFBE37",
                }}
                onClick={() => !parcel.placed && handleParcelClick(parcel)}
              >
                {incorrectParcel === parcel.id ? (
                  <X className="h-12 w-12 text-red-500" />
                ) : (
                  <Box className="h-12 w-12" />
                )}
                <p className="text-center font-bold">{parcel.value}</p>
              </div>
            ))}
          </div>

          {/* Parcel Bins */}
          <div className="mb-8 grid grid-cols-3 gap-4 rounded-lg border border-dashed p-4 md:grid-cols-6">
            {parcelBins.map((bin, index) => (
              <div
                key={index}
                className={`border-2 ${
                  bin.value !== null
                    ? bin.isCorrect
                      ? "border-green-500"
                      : "border-red-500"
                    : "border-dashed border-gray-300"
                } flex h-[80px] w-[80px] flex-col items-center justify-center rounded-lg p-2`}
                style={{
                  backgroundColor:
                    bin.value !== null ? (bin.isCorrect ? "#E8F5E9" : "#FFEBEE") : "transparent",
                }}
              >
                {bin.value !== null ? (
                  <>
                    <Box className="h-10 w-10" />
                    <p className="text-center font-bold">{bin.value}</p>
                  </>
                ) : (
                  <p className="text-center font-bold">#{index + 1}</p>
                )}
              </div>
            ))}
          </div>

          {/* Complete state */}
          {isComplete && (
            <div className="mt-6 text-center">
              <p className="text-xl font-bold">Task Complete!</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ParcelSort
